// ---------------------------------------------------------------------------
// SpeedyDock SDK — Utility helpers
// ---------------------------------------------------------------------------

import { SDK_PREFIX } from "./styles";
import type { EmbedEventType, EmbedEventHandler, PostMessagePayload } from "./types";
import { isDebugEnabled, debugLog } from "./debug";

// ---------------------------------------------------------------------------
// URL builder
// ---------------------------------------------------------------------------

export function buildEmbedUrl(
  baseUrl: string,
  shortName: string,
  opts: {
    date?: string;
    boatSlug?: string;
    locationId?: number;
    categoryId?: number | string;
    conciergeId?: number;
    step?: string;
    primaryColor?: string;
  } = {},
): string {
  let path = `/${encodeURIComponent(shortName)}`;

  if (opts.boatSlug) {
    path += `/boat/${encodeURIComponent(opts.boatSlug)}`;
  }

  const params = new URLSearchParams({ embed: "true" });
  if (opts.date) params.set("date", opts.date);
  if (opts.locationId) params.set("location", String(opts.locationId));
  if (opts.categoryId) {
    // The browse page reads "category"; the boat-details page reads "categoryId".
    // Set both so a single categoryId pre-selects/filters correctly on either entry point.
    params.set("category", String(opts.categoryId));
    params.set("categoryId", String(opts.categoryId));
  }
  if (opts.conciergeId) params.set("conciergeid", String(opts.conciergeId));
  if (opts.step) params.set("step", opts.step);
  // Theme accent forwarded to the embedded app so its primary CTAs match the
  // host's SpeedyDock.init({ theme }). URLSearchParams encodes the leading "#".
  if (opts.primaryColor) params.set("primaryColor", opts.primaryColor);

  return `${baseUrl}${path}?${params.toString()}`;
}

// ---------------------------------------------------------------------------
// DOM helpers
// ---------------------------------------------------------------------------

export function resolveElement(
  selectorOrEl: string | HTMLElement | undefined,
): HTMLElement | null {
  if (!selectorOrEl) return null;
  if (typeof selectorOrEl === "string") {
    return document.querySelector<HTMLElement>(selectorOrEl);
  }
  return selectorOrEl;
}

export function createElement<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  attrs?: Record<string, string>,
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      el.setAttribute(k, v);
    }
  }
  return el;
}

/** Debug-only shape describing a fixed/sticky element seen during offset detection. */
interface StickyCandidate {
  sampleX: number;
  tag: string;
  id?: string;
  className?: string;
  position: string;
  top: number;
  bottom: number;
  accepted: boolean;
  rejectedBecause?: string;
}

/**
 * Measure how much of the top of the viewport is covered by a host-page
 * fixed/sticky header (e.g. a site navigation bar). Returns the largest bottom
 * edge of such a bar so the embed can scroll to a point just beneath it instead
 * of hiding its own top edge behind the header. Returns 0 when none is found.
 */
export function detectTopStickyOffset(ignore?: HTMLElement): number {
  if (typeof document === "undefined") return 0;
  if (typeof document.elementsFromPoint !== "function") {
    debugLog("detectTopStickyOffset", { supported: false, offset: 0 });
    return 0;
  }

  const dbg = isDebugEnabled();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const sampleXs = [vw * 0.5, vw * 0.15, vw * 0.85];
  let maxBottom = 0;
  // Debug-only: every fixed/sticky element seen at the sampled points, with the
  // reason it was accepted or rejected — this is what explains an offset of 0
  // when a header is clearly visible (e.g. it's `position: absolute`, or its
  // `top` isn't pinned to 0, or it's taller than the overlay cutoff).
  const candidates: StickyCandidate[] = [];

  for (const x of sampleXs) {
    const stack = document.elementsFromPoint(Math.round(x), 1);
    for (const node of stack) {
      const el = node as HTMLElement;
      if (el === document.documentElement || el === document.body) continue;
      if (ignore && (el === ignore || ignore.contains(el))) continue;

      const position = window.getComputedStyle(el).position;
      const isSticky = position === "fixed" || position === "sticky";
      const rect = el.getBoundingClientRect();
      // Only count bars pinned to the top that occupy a header-sized band,
      // ignoring full-screen fixed overlays.
      const accepted =
        isSticky &&
        rect.top <= 1 &&
        rect.bottom > maxBottom &&
        rect.bottom <= vh * 0.5;

      if (accepted) maxBottom = rect.bottom;

      if (dbg && (isSticky || rect.top <= 1)) {
        candidates.push({
          sampleX: Math.round(x),
          tag: el.tagName.toLowerCase(),
          id: el.id || undefined,
          className:
            typeof el.className === "string" && el.className
              ? el.className
              : undefined,
          position,
          top: Math.round(rect.top),
          bottom: Math.round(rect.bottom),
          accepted,
          rejectedBecause: accepted
            ? undefined
            : !isSticky
              ? `position:${position} (not fixed/sticky)`
              : rect.top > 1
                ? "not-pinned-to-top (rect.top > 1)"
                : rect.bottom > vh * 0.5
                  ? "too-tall (treated as full-screen overlay)"
                  : "shorter than current best candidate",
        });
      }
    }
  }

  const offset = Math.ceil(maxBottom);
  if (dbg) {
    debugLog("detectTopStickyOffset", {
      viewport: { vw: Math.round(vw), vh: Math.round(vh) },
      sampleXs: sampleXs.map((x) => Math.round(x)),
      candidates,
      offset,
    });
  }
  return offset;
}

// ---------------------------------------------------------------------------
// Iframe factory
// ---------------------------------------------------------------------------

export function createIframe(src: string, loading: "eager" | "lazy" = "lazy"): HTMLIFrameElement {
  return createElement("iframe", `${SDK_PREFIX}-iframe`, {
    src,
    allow: "payment; camera; geolocation",
    loading,
    title: "SpeedyDock Boat Rentals",
  });
}

// ---------------------------------------------------------------------------
// Loading spinner
// ---------------------------------------------------------------------------

export function createSpinner(): HTMLDivElement {
  return createElement("div", `${SDK_PREFIX}-spinner`);
}

export function hideSpinner(spinner: HTMLDivElement): void {
  spinner.classList.add("hidden");
  setTimeout(() => spinner.remove(), 300);
}

// ---------------------------------------------------------------------------
// Simple event emitter
// ---------------------------------------------------------------------------

export class EventEmitter {
  private handlers: Map<string, Set<EmbedEventHandler<EmbedEventType>>> =
    new Map();

  on<T extends EmbedEventType>(event: T, handler: EmbedEventHandler<T>): void {
    if (!this.handlers.has(event)) this.handlers.set(event, new Set());
    this.handlers
      .get(event)!
      .add(handler as EmbedEventHandler<EmbedEventType>);
  }

  off<T extends EmbedEventType>(event: T, handler: EmbedEventHandler<T>): void {
    this.handlers
      .get(event)
      ?.delete(handler as EmbedEventHandler<EmbedEventType>);
  }

  emit<T extends EmbedEventType>(
    event: T,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any,
  ): void {
    this.handlers.get(event)?.forEach((h) => h(data));
  }
}

// ---------------------------------------------------------------------------
// PostMessage listener — maps iframe messages to EventEmitter events
// ---------------------------------------------------------------------------

const EVENT_MAP: Record<string, EmbedEventType> = {
  "speedydock:ready": "ready",
  "speedydock:resize": "resize",
  "speedydock:navigate": "navigate",
  "speedydock:purchase": "purchase",
  "speedydock:close": "close",
  "speedydock:error": "error",
  "speedydock:analytics": "analytics",
};

export function listenToIframe(
  iframe: HTMLIFrameElement,
  emitter: EventEmitter,
  opts?: {
    onReady?: () => void;
    onClose?: () => void;
    onScrollRequest?: () => void;
    autoResize?: boolean;
  },
): () => void {
  const handler = (e: MessageEvent) => {
    // Only accept messages from the iframe's origin
    if (iframe.contentWindow !== e.source) return;

    const msg = e.data as PostMessagePayload;
    if (!msg || typeof msg.type !== "string") return;

    // The embedded app can ask the host to scroll it into view (e.g. so a
    // top-anchored error toast isn't left off-screen). This isn't a public
    // embed event, so it's handled directly rather than via the emitter.
    if (msg.type === "speedydock:scroll-into-view") {
      opts?.onScrollRequest?.();
      return;
    }

    const event = EVENT_MAP[msg.type];
    if (!event) return;

    emitter.emit(event, msg.data);

    if (event === "ready" && opts?.onReady) opts.onReady();
    if (event === "close" && opts?.onClose) opts.onClose();
    if (event === "resize" && opts?.autoResize && msg.data?.height) {
      iframe.style.height = `${msg.data.height}px`;
    }
  };

  window.addEventListener("message", handler);
  return () => window.removeEventListener("message", handler);
}

// ---------------------------------------------------------------------------
// Auto-detect base URL from script tag
// ---------------------------------------------------------------------------

export function detectBaseUrl(): string {
  const scripts = document.querySelectorAll("script[src]");
  for (const s of scripts) {
    const src = (s as HTMLScriptElement).src;
    if (src.includes("speedydock") && src.includes("sdk")) {
      const url = new URL(src);
      return url.origin;
    }
  }
  return window.location.origin;
}

export function detectAutoConfig(): { shortName?: string; conciergeId?: number; locationId?: number; categoryId?: number | string } | null {
  const scripts = document.querySelectorAll(
    "script[data-shortname]",
  );
  for (const s of scripts) {
    const el = s as HTMLElement;
    const shortName = el.dataset.shortname;
    if (shortName) {
      const conciergeId = el.dataset.conciergeid ? Number(el.dataset.conciergeid) : undefined;
      const locationId = el.dataset.locationid ? Number(el.dataset.locationid) : undefined;
      // categoryId may be numeric ("13") or a name ("Tiki"). Preserve the
      // string when it isn't a clean number so clubs that key categories
      // by name continue to work.
      let categoryId: number | string | undefined;
      const rawCat = el.dataset.categoryid;
      if (rawCat != null && rawCat !== "") {
        const n = Number(rawCat);
        categoryId = Number.isFinite(n) && String(n) === rawCat ? n : rawCat;
      }
      return { shortName, conciergeId, locationId, categoryId };
    }
  }
  return null;
}
